import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PublicFormService, ComplaintStatus } from '../../../services/public-form.service';

interface TrackStep {
  key: string;
  label: string;
  desc: string;
  icon: 'pending' | 'progress' | 'resolved' | 'closed';
  done: boolean;
  active: boolean;
}

@Component({
  selector: 'app-suivi-plainte',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TranslatePipe, RouterModule],
  templateUrl: './suivi-plainte.html',
  styleUrl: './suivi-plainte.css',
})
export class SuiviPlainteComponent {
  reference = '';
  loading = false;
  error = '';
  complaint: ComplaintStatus | null = null;

  steps: TrackStep[] = [];

  constructor(
    private publicFormService: PublicFormService,
    private translate: TranslateService,
  ) {}

  track() {
    if (!this.reference.trim()) return;
    this.loading = true;
    this.error = '';
    this.complaint = null;
    this.steps = [];

    this.publicFormService.trackComplaint(this.reference.trim()).subscribe({
      next: (res) => {
        this.loading = false;
        this.complaint = res;
        this.buildSteps(res.status);
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 404) {
          this.error = this.translate.instant('suivi_introuvable');
        } else {
          this.error = this.translate.instant('commun_erreur_reseau');
        }
      },
    });
  }

  buildSteps(status: string) {
    const allSteps = [
      { key: 'pending',     labelKey: 'suivi_etape_recue',       descKey: 'suivi_etape_recue_desc',       icon: 'pending'   as const },
      { key: 'in_progress', labelKey: 'suivi_etape_traitement',  descKey: 'suivi_etape_traitement_desc',  icon: 'progress'  as const },
      { key: 'resolved',    labelKey: 'suivi_etape_resolue',     descKey: 'suivi_etape_resolue_desc',     icon: 'resolved'  as const },
      { key: 'closed',      labelKey: 'suivi_etape_cloturee',    descKey: 'suivi_etape_cloturee_desc',    icon: 'closed'    as const },
    ];
    const order = ['pending', 'in_progress', 'resolved', 'closed'];
    const currentIdx = order.indexOf(status);

    this.steps = allSteps.map((s, i) => ({
      key:    s.key,
      label:  s.labelKey,
      desc:   s.descKey,
      icon:   s.icon,
      done:   i < currentIdx,
      active: i === currentIdx,
    }));
  }

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      pending:     'suivi_statut_attente',
      in_progress: 'suivi_statut_traitement',
      resolved:    'suivi_statut_resolue',
      closed:      'suivi_statut_cloturee',
    };
    return this.translate.instant(map[status] ?? status);
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      pending:     'status-pending',
      in_progress: 'status-progress',
      resolved:    'status-resolved',
      closed:      'status-closed',
    };
    return map[status] ?? '';
  }

  getPriorityLabel(priority: string): string {
    const map: Record<string, string> = {
      low:    'commun_priorite_faible',
      medium: 'commun_priorite_moyenne',
      high:   'commun_priorite_elevee',
      urgent: 'commun_priorite_urgente',
    };
    return this.translate.instant(map[priority] ?? priority);
  }

  getSubject(): string {
    if (!this.complaint?.subject) return '';
    const lang = this.translate.getCurrentLang();
    const s = this.complaint.subject as any;
    return s[lang] || s['fr'] || s['en'] || '';
  }

  formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  }

  reset() {
    this.reference = '';
    this.complaint = null;
    this.steps = [];
    this.error = '';
  }
}
